import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Button,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  TableRestaurant as TableIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';
import toast from 'react-hot-toast';
import { AppIconButton } from '../components/ui';

interface Steward {
  id: number;
  username: string;
  email: string;
  name: string;
  stewardCode: string;
  isOnDuty: boolean;
  assignedTables: number;
}

interface VenueTable {
  id: number;
  name: string;
  code: string;
  location: string;
  isAssigned: boolean;
}

const VendorStewardSettings: React.FC = () => {
  const navigate = useNavigate();
  useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stewardViewAllTables, setStewardViewAllTables] = useState(false);
  const [stewards, setStewards] = useState<Steward[]>([]);
  const [selectedSteward, setSelectedSteward] = useState<Steward | null>(null);
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [tables, setTables] = useState<VenueTable[]>([]);
  const [selectedTables, setSelectedTables] = useState<number[]>([]);

  useEffect(() => {
    fetchSettings();
    fetchStewards();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/api/vendor/settings');
      setStewardViewAllTables(response.data.stewardViewAllTables || false);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchStewards = async () => {
    try {
      const response = await api.get('/api/vendor/stewards');
      setStewards(response.data);
    } catch (error) {
      console.error('Error fetching stewards:', error);
    }
  };

  const fetchTablesForAssignment = async (stewardId: number) => {
    try {
      const response = await api.get('/api/vendor/tables-for-assignment', {
        params: { stewardId }
      });
      const items: VenueTable[] = response.data;
      setTables(items);
      setSelectedTables(items.filter((t) => t.isAssigned).map((t) => t.id));
    } catch (error) {
      console.error('Error fetching tables:', error);
      toast.error('Failed to load tables');
    }
  };

  const handleToggleChange = async () => {
    setSaving(true);
    try {
      await api.put('/api/vendor/settings', {
        stewardViewAllTables: !stewardViewAllTables
      });
      setStewardViewAllTables(!stewardViewAllTables);
      toast.success('Settings updated successfully');
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleAssignTables = async () => {
    if (!selectedSteward) return;
    
    try {
      await api.post(`/api/vendor/stewards/${selectedSteward.id}/assign-tables`, {
        tableIds: selectedTables
      });
      toast.success('Tables assigned successfully');
      setAssignmentDialogOpen(false);
      fetchStewards();
    } catch (error) {
      console.error('Error assigning tables:', error);
      toast.error('Failed to assign tables');
    }
  };

  const openAssignmentDialog = (steward: Steward) => {
    setSelectedSteward(steward);
    setAssignmentDialogOpen(true);
    fetchTablesForAssignment(steward.id);
  };

  const handleTableToggle = (tableId: number) => {
    setSelectedTables(prev => {
      if (prev.includes(tableId)) {
        return prev.filter(id => id !== tableId);
      } else {
        return [...prev, tableId];
      }
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box 
        display="flex" 
        alignItems={{ xs: 'stretch', sm: 'center' }} 
        justifyContent="space-between" 
        gap={2}
        mb={3}
        flexDirection={{ xs: 'column', sm: 'row' }}
      >
        <Typography variant="h5" fontWeight="bold">
          Steward Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<SettingsIcon />}
          onClick={() => navigate('/vendor/settings')}
          size="small"
          sx={{ 
            alignSelf: { xs: 'flex-start', sm: 'auto' },
            textTransform: 'none !important',
            fontWeight: '600 !important',
            px: 3,
            py: 1.5,
            backgroundColor: 'rgba(96, 165, 250, 0.15) !important',
            color: 'var(--text-link) !important',
            borderWidth: '1.5px !important',
            borderStyle: 'solid !important',
            borderColor: 'rgba(96, 165, 250, 0.4) !important',
            boxShadow: 'none !important',
            '&:hover': {
              backgroundColor: 'rgba(96, 165, 250, 0.25) !important',
              borderColor: 'rgba(96, 165, 250, 0.5) !important',
              boxShadow: 'none !important'
            }
          }}
        >
          Back to Settings
        </Button>
      </Box>

      {/* Visibility Settings */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Table Visibility Settings
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Control how stewards view tables in your restaurant
              </Typography>
            </Box>
            <SettingsIcon color="action" />
          </Box>

          <Divider sx={{ my: 2 }} />

          <FormControlLabel
            control={
              <Switch
                checked={stewardViewAllTables}
                onChange={handleToggleChange}
                disabled={saving}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body2" fontWeight="medium">
                  Allow all stewards to view all tables
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stewardViewAllTables
                    ? 'All stewards can see and manage orders for all tables'
                    : 'Stewards can only see tables assigned to them'}
                </Typography>
              </Box>
            }
          />

          {!stewardViewAllTables && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                When disabled, you need to assign specific tables to each steward below.
                Stewards will only see orders for their assigned tables.
              </Typography>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Stewards List */}
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="subtitle1">
              Stewards
            </Typography>
            <Chip
              label={`${stewards.filter(s => s.isOnDuty).length} on duty`}
              color="success"
              size="small"
            />
          </Box>

          {stewards.length === 0 ? (
            <Alert severity="info">
              No stewards found. Create steward accounts from the user management page.
            </Alert>
          ) : (
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ width: '100%' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Steward</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Code</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Status</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Assigned</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stewards.map((steward) => (
                    <TableRow key={steward.id} hover>
                      <TableCell sx={{ maxWidth: 200 }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <PersonIcon fontSize="small" color="action" />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" fontWeight="medium" noWrap>
                              {steward.name || steward.username}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {steward.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={steward.stewardCode} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={steward.isOnDuty ? 'On Duty' : 'Off Duty'}
                          color={steward.isOnDuty ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Chip 
                          icon={<TableIcon fontSize="small" />} 
                          label={`${steward.assignedTables} tables`} 
                          size="small" 
                          variant="outlined" 
                        />
                      </TableCell>
                      <TableCell align="right">
                        <AppIconButton
                          icon={<AssignmentIcon />}
                          tooltip={stewardViewAllTables ? 'Table assignment disabled when all tables are visible' : 'Assign tables'}
                          variant="edit"
                          size="small"
                          onClick={() => {
                            if (stewardViewAllTables) {
                              toast("Disable 'Allow all stewards to view all tables' to assign tables.");
                              return;
                            }
                            openAssignmentDialog(steward);
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Table Assignment Dialog */}
      <Dialog
        open={assignmentDialogOpen}
        onClose={() => setAssignmentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Assign Tables to {selectedSteward?.name || selectedSteward?.username}
        </DialogTitle>
        <DialogContent sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select which tables this steward can manage
          </Typography>
          <List>
            {tables.map((table) => (
              <ListItem key={table.id} button onClick={() => handleTableToggle(table.id)} sx={{ minHeight: 48 }}>
                <ListItemText
                  primary={table.name}
                  secondary={table.location}
                />
                <ListItemSecondaryAction>
                  <Checkbox
                    edge="end"
                    checked={selectedTables.includes(table.id)}
                    onChange={() => handleTableToggle(table.id)}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignmentDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleAssignTables} variant="contained">
            Save Assignments
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VendorStewardSettings;